import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ShieldAlert } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, FetchParams, FetchResult } from '@/components/DataTable/types'
import { fetchCountries, type CountryRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A11.18 — Sanksiya ro'yxatlari. OFAC/EU keng-tarqalgan sanksiyalangan davlat
 * kodlari (statik ma'lumotnoma) mavjud davlat ro'yxatiga QARShI tekshiriladi
 * (`GET /admin/countries`). «Muvofiqlik»: sanksiyalangan davlat BLOKLANGAN bo'lsa
 * OK, aks holda REVIEW (ochiq qolgan — ko'rib chiqish kerak). Read-only,
 * COUNTRIES_VIEW-gated. To'liq avto-sanksiya-feed = DEPS (OFAC/EU API).
 */
const SANCTIONED = new Set([
  'KP', 'IR', 'SY', 'CU', 'RU', 'BY', 'VE', 'MM', 'AF', 'SD', 'SS', 'LY', 'SO', 'YE', 'ZW', 'CD', 'CF', 'ML', 'NI', 'ER',
])

async function fetchSanctioned(params: FetchParams): Promise<FetchResult<CountryRow>> {
  const chunk = await fetchCountries({ ...params, page: 1, pageSize: 300 })
  const rows = chunk.rows.filter((r) => SANCTIONED.has((r.code ?? '').toUpperCase()))
  const start = (params.page - 1) * params.pageSize
  return { rows: rows.slice(start, start + params.pageSize), total: rows.length }
}

export function GeoSanctions() {
  const t = useT()

  const columns = useMemo<AdminColumn<CountryRow>[]>(
    () => [
      { key: 'flag', header: '', type: 'text', width: 44, sortable: false, filterable: false, render: (r) => <span className="text-base">{r.flag || '🌐'}</span> },
      { key: 'code', header: t.col.code, type: 'id', width: 80 },
      { key: 'name', header: t.col.name, type: 'text', minWidth: 180 },
      { key: 'users', header: t.geo.impactUsers, type: 'number', width: 120 },
      {
        key: 'is_blocked',
        header: t.col.status,
        type: 'enum',
        width: 120,
        accessor: (r) => (r.is_blocked ? 'blocked' : 'open'),
        options: [
          { value: 'blocked', label: t.geo.block, status: 'blocked' },
          { value: 'open', label: t.geo.unblock, status: 'active' },
        ],
      },
      {
        key: 'compliance',
        header: t.geo.compliance,
        type: 'enum',
        width: 150,
        accessor: (r) => (r.is_blocked ? 'ok' : 'review'),
        options: [
          { value: 'ok', label: t.geo.compliant, status: 'approved' },
          { value: 'review', label: t.geo.reviewNeeded, status: 'review' },
        ],
      },
    ],
    [t],
  )

  const fetcher = useCallback(fetchSanctioned, [])

  return (
    <>
      <PageHeader
        title={t.geo.sanctionsTitle}
        screenId="A11.18"
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.geo}
          </Link>
        }
      />
      <p className="border-b border-border-subtle px-3 py-2 text-xs text-tertiary">{t.geo.sanctionsNote}</p>
      <DataTable<CountryRow>
        screenId="A11.18"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.code}
        exportable
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
